import ButtonPrimary, {
  EButtonSize,
} from "@rentiohq/shared-frontend/dist/components/components/ButtonPrimary";
import FullScreenActivityIndicator from "@rentiohq/shared-frontend/dist/components/components/FullScreenActivityIndicator";
import { FullScreenErrorState } from "@rentiohq/shared-frontend/dist/components/components/FullScreenState";
import { PaymentRequestFetch } from "@rentiohq/shared-frontend/dist/components/components/PaymentRequestFetch";
import { PropertyFetchListItem } from "@rentiohq/shared-frontend/dist/components/components/PropertyFetchListItem";
import Spacer, {
  ESpacerWeight,
} from "@rentiohq/shared-frontend/dist/components/components/Spacer";
import * as requirementsSelectors from "@rentiohq/shared-frontend/dist/redux/requirements/requirements.selectors";
import { IPaymentRequest } from "@rentiohq/shared-frontend/dist/types/paymentRequest.types";
import { IProperty } from "@rentiohq/shared-frontend/dist/types/property.types";
import { differenceInBusinessDays } from "@rentiohq/shared-frontend/dist/utils/date-fns.utils";
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import queryString from "query-string";
import { useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router";
import { IRootState } from "redux/reducers";
import InfoHeader from "../../../../components/InfoHeader";
import Page from "../../../../components/Page";
import withKycRequired from "../../../../hoc/withKycRequired";
import PaymentRequestRedirect from "../../components/PaymentRequestRedirect";
import Receipt from "../../components/Receipt";
import * as infoUtils from "../../utils/info.utils";
import * as S from "./PaymentOverview.styles";

const NOW = new Date();

const PaymentOverviewBase = () => {
  const navigate = useNavigate();
  const params = useParams();
  const location = useLocation();

  const requirements = useSelector((state: IRootState) =>
    requirementsSelectors.getRequirements(state),
  );

  // Data
  const paymentRequestId = params.paymentRequestId
    ? parseInt(params.paymentRequestId, 0)
    : undefined;

  const { publicId } = queryString.parse(location.search) as {
    publicId?: string;
  };

  // Event handlers
  const handlePressNext = (paymentRequest: IPaymentRequest) => () => {
    navigate(`/payment/${paymentRequest.id}/payment-method`);
  };

  // Render
  const renderPaymentRequestAndProperty = (
    paymentRequest: IPaymentRequest,
    property?: IProperty,
  ) => {
    const body = infoUtils.getBody({
      postfix: "payment_overview",
      paymentRequest,
      property,
      withAllowedPaymentMethodsVariation: true,
    });

    const isPaymentDueNow =
      differenceInBusinessDays(paymentRequest.dueDateAt, NOW) <= 3;

    // Only show extra body for payments now due
    const bodyExtra = isPaymentDueNow
      ? infoUtils.getBodyExtra({
          postfix: "payment_overview",
          paymentRequest,
          property,
          withAllowedPaymentMethodsVariation: true,
        })
      : undefined;

    return (
      <Page>
        <InfoHeader
          title={infoUtils.getTitle({
            postfix: "payment_overview",
            paymentRequest,
            property,
            withAllowedPaymentMethodsVariation: true,
          })}
          body={
            <>
              {[body, bodyExtra]
                .filter(text => !!text)
                .map((text, index) => (
                  <>
                    {index > 0 && <br />}
                    {text}
                  </>
                ))}
            </>
          }
        />

        <Spacer weight={ESpacerWeight.W24} />
        <Receipt
          paymentRequest={paymentRequest}
          property={property}
          sepaBankDaysOffset={requirements?.sepaBankDaysOffset}
        />
        <Spacer weight={ESpacerWeight.W24} />

        <S.ButtonPrimaryWrap>
          <ButtonPrimary
            title={getLocalizedText("payment.online.payment_overview.cta.next")}
            onPress={handlePressNext(paymentRequest)}
            isLoading={false}
            buttonSize={EButtonSize.Full}
          />
        </S.ButtonPrimaryWrap>
      </Page>
    );
  };

  const renderPaymentRequest = (paymentRequest: IPaymentRequest) => (
    <PropertyFetchListItem
      shouldRefetch={false}
      propertyId={paymentRequest.requestPropertyId}
      renderAlways={(property?: IProperty) =>
        renderPaymentRequestAndProperty(paymentRequest, property)
      }
    />
  );

  const renderLoading = () => (
    <Page withScroll={false}>
      <FullScreenActivityIndicator />
    </Page>
  );

  const renderError = (_: () => void, error?: Error) => (
    <Page>
      <FullScreenErrorState
        error={error}
        showIcon={false}
        title={getLocalizedText("general.error")}
      />
    </Page>
  );

  if (!paymentRequestId) {
    return null;
  }

  return (
    <>
      <PaymentRequestRedirect id={paymentRequestId} />
      <PaymentRequestFetch
        id={paymentRequestId}
        publicId={publicId}
        renderPaymentRequest={renderPaymentRequest}
        renderLoading={renderLoading}
        renderError={renderError}
        renderNotLoaded={renderLoading}
        paymentRequestV2Enabled={true}
      />
    </>
  );
};

export const PaymentOverview = withKycRequired(PaymentOverviewBase);
